import { AuthenticationService } from 'src/app/core/services/auth.service';
import { CommConfigService } from 'src/app/core/services/utelize/system/communication/comm-config-service';
import { CommunicationTemplateReplacements } from 'src/app/core/models/system/communication/communication-template-replacements';
import { Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ConfigSystemParams, msSize } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { CustomiseMessageComponent } from 'src/app/shared/customise-message/customise-message.component';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { MessageDefaultDeliveryTypes } from 'src/app/core/enums';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs';
import { TypeMessageDTO } from 'src/app/core/models/system/type/type-message.dto';
import { TypesMessageDefaultDeliveryService } from 'src/app/core/services/utelize/system/type/types-message-default-delivery-service';
import { TypesMessageService } from 'src/app/core/services/utelize/system/type/types-message-service';

/**
 * Types Message Modal Form
 * Edits a message type, including the system wide default wording - what goes out when a company has configured no
 * template of its own. Uses the same editor as the provisioning screens, so the insert chips and the unknown
 * placeholder warning come with it.
 */
//RBW_20261020 - Replaces the grid's own popup (dgPopup.overridePopup) because a message body cannot be edited in a
//               DevExtreme form item, and holding it behind a separate row button left the wording invisible to
//               anybody who opened the row to edit it.
@Component({
  selector: 'app-types-message-modal-form',
  templateUrl: './types-message-modal-form.component.html'
})

export class TypesMessageModalFormComponent implements OnInit {

  @ViewChild('typesMessageModalFormContent', { static: false }) typesMessageModalFormContent!: TemplateRef<void>;

  @Output() typesMessageDataHasBeenSaved: EventEmitter<void>=new EventEmitter<void>();
  @Output() typesMessageFormCancelled   : EventEmitter<void>=new EventEmitter<void>();

  get modalBackGround() { let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
                          return value=value.toUpperCase()=="STATIC" ? "static" : (value.toUpperCase()=="TRUE" ? true : false)     ; }

  private typeMessage             : TypeMessageDTO | undefined;
  private typesMessageModalFormRef: NgbModalRef | undefined;

  public customiseTitle       : string="Default message";
  public defaultMessage       : string="";
  public deliveryTypes        : IdAndDescriptionDTO[]=[];
  public formSubmitted        : boolean=false;
  public formTitle            : string="Message Type";
  //RBW_20261022 - eTypesMessage.MessageType is varchar(255). 20 was simply wrong: "Location Accuracy Change" is 24
  //               characters, so the form was invalid the moment it opened and Submit could never enable.
  public messageTypeMaxLength : number=255;
  public originalMessage      : string="";
  public replacements         : CommunicationTemplateReplacements[]=[];
  public saving               : boolean=false;
  public subjectMaxLength     : number=255;
  public typesMessageForm!    : UntypedFormGroup;

  constructor(
    private authenticationService : AuthenticationService,
    private commConfigService     : CommConfigService,
    private configSystemService   : ConfigSystemService,
    private defaultDeliveryService: TypesMessageDefaultDeliveryService,
    private exceptionsService     : ExceptionsService,
    private formBuilder           : UntypedFormBuilder,
    private modalService          : NgbModal,
    private typesMessageService   : TypesMessageService
  ) { }

  public ngOnInit(): void {
    try {
      this.setUpForm();
      this.loadDeliveryTypes();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Form

  get formData() {
    return this.typesMessageForm.controls;
  }

  get allowDefaultMessage(): boolean {
    return this.typesMessageForm?.controls["allowDefaultMessage"]?.value===true;
  }

  //RBW_20261020 - A subject is only ever used by email, so it is hidden for an SMS only default rather than left on
  //               screen looking like something that still needs filling in.
  get showSubject(): boolean {
    const deliveryType=Number(this.typesMessageForm?.controls["defaultDeliveryTypes"]?.value ?? 0);

    return deliveryType==MessageDefaultDeliveryTypes.Email || deliveryType==MessageDefaultDeliveryTypes.Both;
  }

  //RBW_20261020 - The same check the panel shows in its own warning, asked of our state rather than of the rendered
  //               panel, so the Submit button is right from the first render.
  get messageIsValid(): boolean {
    return !this.allowDefaultMessage || CustomiseMessageComponent.messageIsValid(this.defaultMessage, this.replacements);
  }

  get canSubmit(): boolean {
    return this.typesMessageForm?.valid===true && this.messageIsValid && !this.saving;
  }

  private setUpForm(): void {
    try {
      this.typesMessageForm=this.formBuilder.group({
        active              : new FormControl(true , []),
        allowDefaultMessage : new FormControl(false, []),
        defaultDeliveryTypes: new FormControl(null , []),
        defaultSubject      : new FormControl(""   , [Validators.maxLength(this.subjectMaxLength)]),
        messageType         : new FormControl(""   , [Validators.required, Validators.maxLength(this.messageTypeMaxLength)]),
        singleTemplate      : new FormControl(false, [])
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private loadDeliveryTypes(): void {
    try {
      this.defaultDeliveryService.getIdAndDescription()
      .pipe(take(1))
      .subscribe({
        next : data  => { this.deliveryTypes=data ?? []; },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private populateFormFields(): void {
    try {
      this.typesMessageForm.controls["active"              ].setValue(this.typeMessage?.active               ?? true );
      this.typesMessageForm.controls["allowDefaultMessage" ].setValue(this.typeMessage?.allowDefaultMessage  ?? false);
      this.typesMessageForm.controls["defaultDeliveryTypes"].setValue(this.typeMessage?.defaultDeliveryTypes ?? null );
      this.typesMessageForm.controls["defaultSubject"      ].setValue(this.typeMessage?.defaultSubject       ?? ""   );
      this.typesMessageForm.controls["messageType"         ].setValue(this.typeMessage?.messageType          ?? ""   );
      this.typesMessageForm.controls["singleTemplate"      ].setValue(this.typeMessage?.singleTemplate       ?? false);

      this.applyDefaultValidators();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //RBW_20261020 - How the default is sent only matters once the type allows one, so the field is required while the
  //               switch is on and carries no validation at all when it is off.
  private applyDefaultValidators(): void {
    try {
      const deliveryTypes=this.typesMessageForm.controls["defaultDeliveryTypes"];

      deliveryTypes.setValidators(this.allowDefaultMessage ? [Validators.required] : []);
      deliveryTypes.updateValueAndValidity();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onAllowDefaultMessageChanged(): void {
    try {
      this.applyDefaultValidators();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Open

  public openForm(typeMessage: TypeMessageDTO): void {
    try {
      this.typeMessage   =typeMessage;
      this.defaultMessage=typeMessage?.defaultMessage ?? "";
      this.formSubmitted =false;
      this.customiseTitle="Default message for " + (typeMessage?.messageType ?? "");
      this.replacements  =[];
      this.saving        =false;

      //RBW_20261020 - Reset restores what was there when the modal opened, not a factory default: there is nothing
      //               further back to go to once this IS the default.
      this.originalMessage=this.defaultMessage;

      this.setUpForm();
      this.populateFormFields();
      this.loadReplacements();

      this.typesMessageModalFormRef=this.modalService.open(this.typesMessageModalFormContent,
        {
          backdrop: this.modalBackGround,
          centered: true,
          keyboard: false,
          size    : msSize.Large
        });

      //RBW_20261020 - Dismissing on the backdrop rejects the result rather than closing it, so the screen is told
      //               here as well - without it the row could never be opened a second time.
      this.typesMessageModalFormRef.result.catch(() => { this.typesMessageFormCancelled.emit(); });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //RBW_20261020 - The placeholders valid for THIS message type. Without them every @@value would be reported as
  //               unknown, so the panel is left with an empty list only if the call fails.
  private loadReplacements(): void {
    try {
      const userId=this.authenticationService.currentUserObject().userId;

      this.commConfigService.getCommunicationTemplateReplacements(userId, this.typeMessage?.id ?? -1)
      .pipe(take(1))
      .subscribe({
        next : data  => { this.replacements=data ?? []; },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Submit/Cancel

  public typesMessageFormSubmitAttempt(): void {
    try {
      if (this.saving)
        return;

      this.formSubmitted=true;

      if (!this.canSubmit)
        return;

      this.saving=true;
      this.save();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private save(): void {
    try {
      if (this.typeMessage==undefined) {
        this.saving=false;
        return;
      }

      //RBW_20261020 - Sent as a whole row because the API's Put replaces the record; sending only the edited fields
      //               would blank everything else held against the message type.
      const updated: TypeMessageDTO={
        ...this.typeMessage,
        active              : this.typesMessageForm.controls["active"              ].value===true,
        allowDefaultMessage : this.typesMessageForm.controls["allowDefaultMessage" ].value===true,
        defaultDeliveryTypes: this.typesMessageForm.controls["defaultDeliveryTypes"].value ?? null,
        defaultMessage      : this.defaultMessage,
        defaultSubject      : this.typesMessageForm.controls["defaultSubject"      ].value ?? "",
        messageType         : this.typesMessageForm.controls["messageType"         ].value,
        singleTemplate      : this.typesMessageForm.controls["singleTemplate"      ].value===true
      };

      this.typesMessageService.put(updated)
      .pipe(take(1))
      .subscribe({
        next : () => {
                       this.saving=false;
                       this.typesMessageDataHasBeenSaved.emit();
                       this.closeModal();
                     },
        error: error => {
                          this.saving=false;
                          this.exceptionsService.handleException(new Error(error));
                        }
      });
    }
    catch(e) {
      this.saving=false;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public typesMessageFormCancel(): void {
    try {
      this.typesMessageFormCancelled.emit();
      this.closeModal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private closeModal(): void {
    try {
      this.typesMessageModalFormRef?.close();
      this.typesMessageModalFormRef=undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
